import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Search, Users, ShieldCheck, GraduationCap, KeyRound, Settings2 } from 'lucide-react'
import { usersApi } from '@/api/users'
import { orgsApi } from '@/api/orgs'
import { ADMIN_EMAIL } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { ResendChannel } from '@/api/resendInvite'
import ResendInviteButtons from '@/components/ResendInviteButtons'
import ResetPasswordModal, { type ResetTarget } from '@/pages/orgs/tabs/ResetPasswordModal'
import type { UserListItem } from '@/types'
import OpsMobileNav from '@/components/OpsMobileNav'
import UserAccessActions from '@/components/UserAccessActions'

const ROLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'admin', label: 'Admin' },
  { value: 'student', label: 'Student' },
  { value: 'instructor', label: 'Instructor' },
]

const LOGIN_RANGES = [
  { value: '', label: 'Any time' },
  { value: '1', label: 'Today' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: 'never', label: 'Never logged in' },
]

function roleBadge(role: string | null) {
  if (!role) return <span className="text-xs text-gray-500">—</span>
  const map: Record<string, { bg: string; color: string }> = {
    admin: { bg: '#6366f120', color: '#a5b4fc' },
    student: { bg: '#0d948820', color: '#2dd4bf' },
    instructor: { bg: '#d9770620', color: '#fbbf24' },
  }
  const style = map[role] ?? { bg: '#ffffff15', color: '#9ca3af' }
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium capitalize"
      style={{ background: style.bg, color: style.color }}
    >
      {role}
    </span>
  )
}

function statusBadge(status: string | null) {
  if (!status) return <span className="text-xs text-gray-500">—</span>
  const isActive = status === 'active'
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium capitalize"
      style={{
        background: isActive ? '#22c55e20' : '#ffffff15',
        color: isActive ? '#4ade80' : '#9ca3af',
      }}
    >
      {status}
    </span>
  )
}

function typeBadge(orgRole: string | null, fallbackRole: string | null) {
  // Owner / Member come from org_users; otherwise fall back to the user role.
  if (orgRole === 'owner') {
    return (
      <span
        className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium"
        style={{ background: '#d9770620', color: '#fbbf24' }}
      >
        Owner
      </span>
    )
  }
  if (orgRole) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
        style={{ background: '#6366f120', color: '#a5b4fc' }}
      >
        Member
        <span className="text-gray-400 capitalize">· {orgRole}</span>
      </span>
    )
  }
  if (fallbackRole) {
    return (
      <span
        className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium capitalize"
        style={{ background: '#ffffff15', color: '#9ca3af' }}
      >
        {fallbackRole}
      </span>
    )
  }
  return <span className="text-xs text-gray-500">—</span>
}

function displayName(user: UserListItem): string {
  if (user.first_name || user.last_name) {
    return [user.first_name, user.last_name].filter(Boolean).join(' ')
  }
  return user.name || '—'
}

function formatLastLogin(ts: string | null): string {
  if (!ts) return '—'
  const d = new Date(ts)
  const diffMs = Date.now() - d.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 30) return `${diffDays}d ago`
  return d.toISOString().slice(0, 10)
}

export default function UsersPage() {
  const { user } = useAuth()
  const canResetPassword = user?.email === ADMIN_EMAIL
  const [search, setSearch] = useState('')
  const [role, setRole] = useState('')
  const [orgFilter, setOrgFilter] = useState('')
  const [loginRange, setLoginRange] = useState('')
  const [resetTarget, setResetTarget] = useState<ResetTarget | null>(null)
  const [accessTarget, setAccessTarget] = useState<UserListItem | null>(null)

  const { data: stats } = useQuery({
    queryKey: ['admin-user-stats'],
    queryFn: () => usersApi.getUserStats(),
    staleTime: 60_000,
  })

  const { data: users = [], isLoading, isError, error } = useQuery({
    queryKey: ['admin-users', search, role],
    queryFn: () => usersApi.listUsers({ search: search || undefined, role: role || undefined }),
    staleTime: 30_000,
  })

  const { data: lastLogins = {} } = useQuery({
    queryKey: ['admin-users-last-login'],
    queryFn: () => usersApi.listLastLogins(),
    staleTime: 60_000,
  })

  const { data: orgsData } = useQuery({
    queryKey: ['orgs-for-filter'],
    queryFn: () => orgsApi.list({ perPage: 200 }),
    staleTime: 5 * 60_000,
  })

  const { data: orgRoles = [] } = useQuery({
    queryKey: ['admin-org-roles'],
    queryFn: () => usersApi.listOrgRoles(),
    staleTime: 60_000,
  })

  // Map each user to its highest-priority org role (owner wins over others).
  const roleByUser = useMemo(() => {
    const map: Record<string, string> = {}
    for (const r of orgRoles) {
      if (!map[r.user_id] || r.role === 'owner') map[r.user_id] = r.role
    }
    return map
  }, [orgRoles])

  const filtered = useMemo(() => {
    return users.filter(u => {
      if (orgFilter && u.organization_id !== orgFilter) return false
      if (loginRange) {
        const last = lastLogins[u.id] ?? null
        if (loginRange === 'never') {
          if (last) return false
        } else {
          const days = parseInt(loginRange, 10)
          if (!last) return false
          const diffDays = (Date.now() - new Date(last).getTime()) / (1000 * 60 * 60 * 24)
          if (diffDays > days) return false
        }
      }
      return true
    })
  }, [users, orgFilter, loginRange, lastLogins])

  const handleResendUser = async (userId: string, channel: ResendChannel) => {
    try {
      const { sent_to } = await usersApi.resendUserInvite(userId, channel)
      toast.success(`${channel === 'email' ? 'Email' : 'WhatsApp'} sent to ${sent_to}`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to resend invite')
    }
  }

  return (
    <div className="space-y-6">
      <OpsMobileNav />
      <ResetPasswordModal
        member={resetTarget}
        isOpen={!!resetTarget}
        onClose={() => setResetTarget(null)}
      />
      <UserAccessActions
        user={accessTarget}
        isOpen={!!accessTarget}
        onClose={() => setAccessTarget(null)}
        onResetPassword={(target) => {
          setAccessTarget(null)
          setResetTarget({
            auth_user_id: target.auth_user_id,
            name: target.name,
            email: target.email,
            first_name: target.first_name,
            last_name: target.last_name,
          })
        }}
      />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Users</h1>
        <p className="text-sm text-gray-400 mt-1">All registered users across the platform</p>
      </div>

      {/* Stat chips */}
      <div className="flex flex-wrap gap-3">
        <div
          className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm"
          style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
        >
          <Users size={16} style={{ color: '#6366f1' }} />
          <span className="text-gray-400">Total Users</span>
          <span className="font-semibold text-white ml-1">{stats?.total_users ?? '—'}</span>
        </div>
        <div
          className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm"
          style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
        >
          <ShieldCheck size={16} style={{ color: '#a5b4fc' }} />
          <span className="text-gray-400">Admins</span>
          <span className="font-semibold text-white ml-1">{stats?.admin_count ?? '—'}</span>
        </div>
        <div
          className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm"
          style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
        >
          <GraduationCap size={16} style={{ color: '#2dd4bf' }} />
          <span className="text-gray-400">Students</span>
          <span className="font-semibold text-white ml-1">{stats?.student_count ?? '—'}</span>
        </div>
        <div
          className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm"
          style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
        >
          <span className="text-gray-400">Showing</span>
          <span className="font-semibold text-white ml-1">{filtered.length}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div
          className="flex items-center gap-2 flex-1 px-3 py-2 rounded-lg"
          style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
        >
          <Search size={15} className="text-gray-500 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-transparent text-sm text-white placeholder-gray-500 outline-none flex-1"
          />
        </div>
        <select
          value={role}
          onChange={e => setRole(e.target.value)}
          className="px-3 py-2 rounded-lg text-sm text-gray-300 outline-none"
          style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
        >
          {ROLE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={orgFilter}
          onChange={e => setOrgFilter(e.target.value)}
          className="px-3 py-2 rounded-lg text-sm text-gray-300 outline-none min-w-[12rem]"
          style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
        >
          <option value="">All Organizations</option>
          {(orgsData?.items ?? []).map(o => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
        <select
          value={loginRange}
          onChange={e => setLoginRange(e.target.value)}
          className="px-3 py-2 rounded-lg text-sm text-gray-300 outline-none"
          style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
        >
          {LOGIN_RANGES.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <Users size={32} className="text-gray-600" />
            <p className="text-sm text-gray-400">Failed to load users</p>
            <p className="text-xs text-gray-500">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <Users size={32} className="text-gray-600" />
            <p className="text-sm text-gray-500">No users found</p>
          </div>
        ) : (
          <>
          <div dir="rtl" className="md:hidden divide-y" style={{ borderColor: '#2a2d3e' }}>
            {filtered.map((target) => (
              <article key={target.id} className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-white">{displayName(target)}</div>
                    <div className="mt-0.5 truncate text-xs text-gray-400" dir="ltr">{target.email}</div>
                    <div className="mt-1 text-xs text-gray-500">{target.org_name ?? 'ללא ארגון'} · {formatLastLogin(lastLogins[target.id] ?? null)}</div>
                  </div>
                  {statusBadge(target.user_status)}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {typeBadge(roleByUser[target.id] ?? null, target.role)}
                  {roleBadge(target.role)}
                </div>
                <button
                  type="button"
                  onClick={() => setAccessTarget(target)}
                  className="flex min-h-10 w-full items-center justify-center gap-2 rounded-lg text-sm font-medium text-indigo-200"
                  style={{ background: '#6366f120', border: '1px solid #6366f140' }}
                >
                  <Settings2 size={15} />
                  ניהול משתמש
                </button>
              </article>
            ))}
          </div>
          <div dir="rtl" className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid #2a2d3e' }}>
                  {['Name', 'Email', 'Type', 'Role', 'Organization', 'Status', 'Last login', 'Created', 'Invite', ...(canResetPassword ? ['Password'] : []), 'Manage'].map(h => (
                    <th
                      key={h}
                      className="px-4 py-3 text-right text-[11px] font-medium uppercase tracking-wider text-gray-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((user, i) => (
                  <tr
                    key={user.id}
                    onClick={() => console.log('user id:', user.id)}
                    className="cursor-pointer hover:bg-white/[0.03] transition-colors"
                    style={i < filtered.length - 1 ? { borderBottom: '1px solid #2a2d3e' } : {}}
                  >
                    <td className="px-4 py-3 font-medium text-white whitespace-nowrap">
                      {displayName(user)}
                    </td>
                    <td className="px-4 py-3 text-gray-400 font-mono text-xs">
                      {user.email}
                    </td>
                    <td className="px-4 py-3">
                      {typeBadge(roleByUser[user.id] ?? null, user.role)}
                    </td>
                    <td className="px-4 py-3">
                      {roleBadge(user.role)}
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {user.org_name ?? <span className="text-gray-600">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {statusBadge(user.user_status)}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                      {formatLastLogin(lastLogins[user.id] ?? null)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <ResendInviteButtons
                        hasEmail={!!user.email}
                        hasPhone
                        onResend={(channel) => handleResendUser(user.id, channel)}
                      />
                    </td>
                    {canResetPassword && (
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          disabled={!user.auth_user_id}
                          title={user.auth_user_id ? 'Reset password' : 'No auth account'}
                          onClick={(e) => {
                            e.stopPropagation()
                            setResetTarget({
                              auth_user_id: user.auth_user_id,
                              name: user.name,
                              email: user.email,
                              first_name: user.first_name,
                              last_name: user.last_name,
                            })
                          }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ background: '#6366f120', color: '#a5b4fc' }}
                        >
                          <KeyRound size={13} />
                          Reset
                        </button>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); setAccessTarget(user) }}
                        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium text-indigo-200"
                        style={{ background: '#6366f120' }}
                      >
                        <Settings2 size={13} />
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  )
}
